import { useState, useCallback, useMemo, useRef, useEffect } from 'react';

export type FilterValue = string | string[];

export interface UseFilteredListOptions<T> {
  items: T[];
  searchFn?: (item: T, query: string) => boolean;
  filterFns?: Record<string, (item: T, value: FilterValue) => boolean>;
  debounceMs?: number;
}

export interface UseFilteredListReturn<T> {
  filteredItems: T[];
  searchValue: string;
  onSearchChange: (value: string) => void;
  filterValues: Record<string, FilterValue>;
  onFilterChange: (key: string, value: FilterValue) => void;
  resetFilters: () => void;
}

export function useFilteredList<T>({
  items,
  searchFn,
  filterFns,
  debounceMs = 200,
}: UseFilteredListOptions<T>): UseFilteredListReturn<T> {
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterValues, setFilterValues] = useState<Record<string, FilterValue>>({});
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const onSearchChange = useCallback(
    (value: string) => {
      setSearchValue(value);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setDebouncedSearch(value), debounceMs);
    },
    [debounceMs],
  );

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const onFilterChange = useCallback((key: string, value: FilterValue) => {
    setFilterValues((prev) => {
      const isEmpty = Array.isArray(value) ? value.length === 0 : !value;
      if (isEmpty) {
        const next = { ...prev };
        delete next[key];
        return next;
      }
      return { ...prev, [key]: value };
    });
  }, []);

  const resetFilters = useCallback(() => {
    setSearchValue('');
    setDebouncedSearch('');
    setFilterValues({});
  }, []);

  const filteredItems = useMemo(() => {
    let result = items;

    if (debouncedSearch && searchFn) {
      result = result.filter((item) => searchFn(item, debouncedSearch));
    }

    if (filterFns) {
      for (const [key, value] of Object.entries(filterValues)) {
        const fn = filterFns[key];
        const isActive = Array.isArray(value) ? value.length > 0 : !!value;
        if (fn && isActive) {
          result = result.filter((item) => fn(item, value));
        }
      }
    }

    return result;
  }, [items, debouncedSearch, searchFn, filterValues, filterFns]);

  return { filteredItems, searchValue, onSearchChange, filterValues, onFilterChange, resetFilters };
}
